import { forwardRef } from 'react';
import { Box, Typography, Divider, Table, TableBody, TableCell, TableHead, TableRow, Grid } from '@mui/material';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));

// A4 Invoice
const A4Invoice = ({ sale, tenant }) => (
  <Box sx={{ p: 4, fontFamily: 'Arial, sans-serif', fontSize: 14, color: '#000' }}>
    {/* Header */}
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
      <Box>
        <Typography variant="h5" fontWeight={700} color="primary">{tenant?.name || 'POS Pro'}</Typography>
        {tenant?.address && <Typography variant="body2">{tenant.address}</Typography>}
        {tenant?.phone && <Typography variant="body2">Tel: {tenant.phone}</Typography>}
        {tenant?.tax_number && <Typography variant="body2">NTN: {tenant.tax_number}</Typography>}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Typography variant="h4" fontWeight={700} color="primary" sx={{ letterSpacing: 1 }}>INVOICE</Typography>
        <Typography variant="h6" fontWeight={600}>{sale.invoiceNumber}</Typography>
        <Typography variant="body2">Date: {new Date().toLocaleDateString('en-PK')}</Typography>
        <Typography variant="body2">Time: {new Date().toLocaleTimeString('en-PK')}</Typography>
      </Box>
    </Box>

    <Divider sx={{ mb: 2, borderWidth: 2, borderColor: 'primary.main' }} />

    {/* Bill To */}
    {sale.customer && (
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={700} mb={0.5}>BILL TO</Typography>
        <Typography fontWeight={600}>{sale.customer.name}</Typography>
        {sale.customer.phone && <Typography variant="body2">{sale.customer.phone}</Typography>}
        {sale.customer.address && <Typography variant="body2">{sale.customer.address}</Typography>}
      </Box>
    )}

    {/* Items Table */}
    <Table size="small" sx={{ mb: 3 }}>
      <TableHead>
        <TableRow sx={{ bgcolor: 'primary.main' }}>
          <TableCell sx={{ color: 'white', fontWeight: 700 }}>#</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 700 }}>Description</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Qty</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Unit Price</TableCell>
          <TableCell sx={{ color: 'white', fontWeight: 700 }} align="right">Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sale.items?.map((item, i) => (
          <TableRow key={i} sx={{ '&:nth-of-type(even)': { bgcolor: '#f9fafb' } }}>
            <TableCell>{i + 1}</TableCell>
            <TableCell>{item.product_name}</TableCell>
            <TableCell align="center">{item.quantity}</TableCell>
            <TableCell align="right">₨ {fmt(item.unit_price)}</TableCell>
            <TableCell align="right">₨ {fmt(item.unit_price * item.quantity)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>

    {/* Totals */}
    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Box sx={{ minWidth: 280 }}>
        {[
          ['Subtotal', `₨ ${fmt(sale.subtotal || sale.total)}`, false],
          ...(sale.discountAmount > 0 ? [['Discount', `- ₨ ${fmt(sale.discountAmount)}`, false]] : []),
          ...(sale.taxAmount > 0 ? [['Tax', `₨ ${fmt(sale.taxAmount)}`, false]] : []),
          ['TOTAL', `₨ ${fmt(sale.total)}`, true],
          ['Paid', `₨ ${fmt(sale.paidAmount || sale.total)}`, false],
          ...(sale.change > 0 ? [['Change', `₨ ${fmt(sale.change)}`, false]] : []),
          ...(sale.due > 0 ? [['Due', `₨ ${fmt(sale.due)}`, false, 'error.main']] : []),
        ].map(([label, value, bold, color]) => (
          <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: bold ? '2px solid' : '1px solid', borderColor: bold ? 'primary.main' : 'divider' }}>
            <Typography fontWeight={bold ? 700 : 400}>{label}</Typography>
            <Typography fontWeight={bold ? 700 : 400} color={color || (bold ? 'primary.main' : 'inherit')}>{value}</Typography>
          </Box>
        ))}
      </Box>
    </Box>

    {/* Footer */}
    <Box sx={{ mt: 4, textAlign: 'center', pt: 2, borderTop: '1px dashed #ccc' }}>
      <Typography variant="body2">{tenant?.receipt_footer || 'Thank you for your business!'}</Typography>
      <Typography variant="body2" sx={{ fontFamily: 'serif', mt: 0.5 }}>شکریہ — آپ کا دوبارہ خیرمقدم ہے</Typography>
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>Powered by POS Pro Pakistan</Typography>
    </Box>
  </Box>
);

// Thermal 80mm / 58mm Receipt
const ThermalReceipt = ({ sale, tenant, width = 80 }) => {
  const rs = tenant?.receipt_settings || {};
  const charWidth = width === 58 ? 32 : 42;
  const baseFontSize = width === 58 ? 9 : (rs.font_size === 'small' ? 10 : rs.font_size === 'large' ? 13 : 11);
  const mono = { fontFamily: 'monospace', fontSize: baseFontSize };
  const line = (left, right) => {
    const gap = charWidth - left.length - right.length;
    return left + ' '.repeat(Math.max(1, gap)) + right;
  };

  const showPhone   = rs.show_phone   !== false;
  const showAddress = rs.show_address !== false;
  const showNTN     = rs.show_ntn     !== false;
  const showCashier = rs.show_cashier !== false;
  const showLoyalty = rs.show_loyalty !== false;

  return (
    <Box sx={{ width: width === 58 ? 200 : 290, fontFamily: 'monospace', fontSize: baseFontSize, p: 1.5, color: '#000' }}>
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Typography sx={{ ...mono, fontSize: baseFontSize + 2, fontWeight: 700 }}>{tenant?.name || 'POS Pro'}</Typography>
        {rs.tagline && <Typography sx={mono}>{rs.tagline}</Typography>}
        {showPhone   && tenant?.phone   && <Typography sx={mono}>Tel: {tenant.phone}</Typography>}
        {showAddress && tenant?.address && <Typography sx={mono}>{tenant.address}</Typography>}
        {showNTN     && tenant?.tax_number && <Typography sx={mono}>NTN: {tenant.tax_number}</Typography>}
        <Typography sx={mono}>{'='.repeat(charWidth)}</Typography>
        <Typography sx={mono}>{sale.invoiceNumber}</Typography>
        <Typography sx={mono}>{new Date().toLocaleString('en-PK')}</Typography>
        {showCashier && sale.cashierName && <Typography sx={mono}>Cashier: {sale.cashierName}</Typography>}
        {sale.customer && <Typography sx={mono}>Customer: {sale.customer.name}</Typography>}
        <Typography sx={mono}>{'-'.repeat(charWidth)}</Typography>
      </Box>

      {sale.items?.map((item, i) => (
        <Box key={i}>
          <Typography sx={{ ...mono, fontWeight: 600 }}>{item.product_name}</Typography>
          <Typography sx={mono}>{line(`  ${item.quantity} x Rs.${fmt(item.unit_price)}`, `Rs.${fmt(item.unit_price * item.quantity)}`)}</Typography>
          {item.discount_amount > 0 && <Typography sx={{ ...mono, color: '#666' }}>{line('  Disc', `-Rs.${fmt(item.discount_amount)}`)}</Typography>}
        </Box>
      ))}

      <Typography sx={mono}>{'-'.repeat(charWidth)}</Typography>
      {sale.discountAmount > 0 && <Typography sx={mono}>{line('Discount', `-Rs.${fmt(sale.discountAmount)}`)}</Typography>}
      {sale.taxAmount > 0 && <Typography sx={mono}>{line(`GST`, `Rs.${fmt(sale.taxAmount)}`)}</Typography>}
      <Typography sx={{ ...mono, fontWeight: 700, fontSize: baseFontSize + 1 }}>{line('TOTAL', `Rs.${fmt(sale.total)}`)}</Typography>
      <Typography sx={mono}>{line(`Paid (${sale.paymentMethod || 'cash'})`, `Rs.${fmt(sale.paidAmount || sale.total)}`)}</Typography>
      {sale.change > 0 && <Typography sx={mono}>{line('Change', `Rs.${fmt(sale.change)}`)}</Typography>}
      {sale.due > 0 && <Typography sx={{ ...mono, fontWeight: 700 }}>{line('*** DUE ***', `Rs.${fmt(sale.due)}`)}</Typography>}

      {showLoyalty && sale.customer?.loyalty_points > 0 && (
        <Typography sx={{ ...mono, color: '#444' }}>{line('Loyalty Pts', `${sale.customer.loyalty_points}`)}</Typography>
      )}

      <Typography sx={mono}>{'='.repeat(charWidth)}</Typography>
      <Box sx={{ textAlign: 'center' }}>
        <Typography sx={mono}>{tenant?.receipt_footer || 'Thank you for your business!'}</Typography>
        {rs.footer_line2 && <Typography sx={mono}>{rs.footer_line2}</Typography>}
        {!rs.footer_line2 && <Typography sx={mono}>شکریہ</Typography>}
      </Box>
    </Box>
  );
};

const InvoicePrint = forwardRef(({ sale, tenant, format = 'thermal80' }, ref) => (
  <Box ref={ref}>
    <style>{`@media print { body * { visibility: hidden; } #invoice-print, #invoice-print * { visibility: visible; } #invoice-print { position: absolute; left: 0; top: 0; } ${format === 'a4' ? '@page { size: A4; margin: 10mm; }' : format === 'thermal58' ? '@page { size: 58mm; margin: 0; }' : '@page { size: 80mm; margin: 0; }'} }`}</style>
    <Box id="invoice-print">
      {format === 'a4' && <A4Invoice sale={sale} tenant={tenant} />}
      {format === 'thermal80' && <ThermalReceipt sale={sale} tenant={tenant} width={80} />}
      {format === 'thermal58' && <ThermalReceipt sale={sale} tenant={tenant} width={58} />}
    </Box>
  </Box>
));

InvoicePrint.displayName = 'InvoicePrint';
export default InvoicePrint;
